import { z } from "zod";
import { type PeriodSelectOption } from "@/lib/period-select";
import { makeRoute } from "@/lib/router/utils";

export const Routes = {
  // Public routes
  Onboarding: makeRoute("/auth/onboarding"),
  PublicExplore: makeRoute("/explore"),

  App: makeRoute("/app"),
  Today: makeRoute("/app/today"),
  Projects: makeRoute("/app/projects", undefined, z.object({ fromUrl: z.string().optional() })),
  Project: makeRoute(
    "/app/projects/:id",
    z.object({ id: z.string().optional() }),
    z.object({
      fromUrl: z.string().optional(),
      origin: z.enum(["upcoming", "projects"]).optional(),
    }),
  ),
  ProjectOverview: makeRoute(
    "/app/projects/:id/overview",
    z.object({ id: z.string().optional() }),
    z.object({
      fromUrl: z.string().optional(),
      // Optional YYYY-MM-DD date of the shift/card the user tapped, so the
      // project detail can open on that date's signup segment instead of the first.
      day: z.string().optional(),
    }),
  ),
  ProjectInfo: makeRoute(
    "/app/projects/:id/info",
    z.object({ id: z.string().optional() }),
    z.object({ fromUrl: z.string().optional() }),
  ),
  ProjectShifts: makeRoute(
    "/app/projects/:id/shifts",
    z.object({ id: z.string().optional() }),
    z.object({ fromUrl: z.string().optional() }),
  ),
  ProjectApplications: makeRoute(
    "/app/projects/:id/applications",
    z.object({ id: z.string().optional() }),
    z.object({ fromUrl: z.string().optional() }),
  ),
  UpcomingShifts: makeRoute("/app/shifts/upcoming"),
  /**
   * A shift is a thing you open, not a row you read.
   *
   * Singular `/app/shift/:id`, deliberately not `/app/shifts/:id`: `IonRouterOutlet` is not a
   * `Switch`. Its `matchRoute` walks every child and keeps the **last** match, so a `:id` under
   * `/app/shifts/` swallows the literal `upcoming` and `completed` routes no matter where it is
   * declared or whether it is `exact`. A separate segment makes the collision impossible instead of
   * ordering around it.
   */
  ShiftDetail: makeRoute(
    "/app/shift/:id",
    z.object({ id: z.string().optional() }),
    // Set when the employee has just said yes to declaring something, so the screen can open the
    // form instead of making them find the button they already agreed to press.
    z.object({ declare: z.coerce.boolean().optional() }),
  ),
  TransferClaim: makeRoute("/app/transfer/:token", z.object({ token: z.string().optional() })),

  // Shifts
  /**
   * The period stays a search param because the legacy Afgerond screen reads it — it is the only
   * screen that ever did, and it is still what an organization without `appReworkCompletedEnabled`
   * gets. The reworked screen renders the same list in two places, only one of which owns a route,
   * so it keeps the period in component state and ignores this param.
   */
  CompletedShifts: makeRoute(
    "/app/shifts/completed",
    undefined,
    z.object({
      period: z
        .enum([
          "this-month",
          "this-year",
          "last-year",
          "all-years",
        ] as const satisfies ReadonlyArray<PeriodSelectOption>)
        .default("this-month"),
    }),
  ),

  // Settings
  Settings: makeRoute("/app/settings"),
  PersonalInformation: makeRoute("/settings/personal-information"),
  Notifications: makeRoute("/settings/notifications"),
  NotificationsDevices: makeRoute("/settings/notifications/devices"),
  Calendar: makeRoute("/settings/calendar"),
  Appearance: makeRoute("/settings/appearance"),
  Language: makeRoute("/settings/language"),
  Privacy: makeRoute("/settings/privacy"),
  Terms: makeRoute("/settings/terms"),

  Auth: makeRoute("/auth"),

  AuthSignIn: makeRoute("/auth/sign-in"),
  AuthSignInPassword: makeRoute(
    "/auth/sign-in/password",
    undefined,
    z.object({
      callbackUrl: z.string().optional(),
    }),
  ),

  AuthSignUpProfile: makeRoute("/auth/sign-up/profile"),

  AuthForgotPassword: makeRoute("/auth/forgot-password"),
  AuthForgotPasswordVerify: makeRoute("/auth/forgot-password/verify"),
  AuthForgotPasswordReset: makeRoute("/auth/reset-password"),
  AuthAcceptInvite: makeRoute(
    "/auth/accept-invitation/:token",
    z.object({
      token: z.string().optional(),
    }),
  ),
  AuthRegister: makeRoute("/auth/register"),
  AuthRegisterSuccess: makeRoute("/auth/register/success"),

  // Organization switching
  SwitchOrganization: makeRoute(
    "/app/switch-organization",
    undefined,
    z.object({
      targetOrgId: z.string(),
      callback: z.string().optional(),
    }),
  ),
};
